import { JourneyStepType, Rule } from '../../../types'
import { EntranceStepIcon } from '../../../ui/icons'
import RadioInput from '../../../ui/form/RadioInput'
import RuleBuilder from '../../lists/rules/RuleBuilder'
import { useCallback, useContext, useMemo } from 'react'
import { PreferencesContext } from '../../../ui/PreferencesContext'
import SwitchField from '../../../ui/form/SwitchField'
import { EntityIdPicker } from '../../../ui/form/EntityIdPicker'
import api from '../../../api'
import { RRule } from 'rrule'
import { useResolver } from '../../../hooks'
import RRuleEditor, { rruleToText } from '../../../ui/RRuleEditor'
import CodeExample from '../../../ui/CodeExample'
import { env } from '../../../config/env'
import { useTranslation, Trans } from 'react-i18next'
import { createEventRule, emptySuggestions, isEventWrapper, VariablesContext } from '../../lists/rules/RuleHelpers'
import { ruleDescription } from '../../lists/rules/RuleDescriptions'
import RuleEventName from '../../lists/rules/RuleEventName'
import { Tag } from '../../../ui'
import { Link } from 'react-router'

interface EntranceConfig {
    trigger: 'none' | 'event' | 'schedule'

    // event based
    event_name?: string
    rule?: Rule
    multiple?: boolean
    concurrent?: boolean

    // schedule based
    list_id?: number
    schedule?: string

    references?: Array<{ id: number, name: string }>
}

const getTriggerOptions = (t: (key: string) => string) => [
    {
        key: 'none',
        label: t('trigger_none'),
    },
    {
        key: 'event',
        label: t('event'),
    },
    {
        key: 'schedule',
        label: t('schedule'),
    },
]

const codeExample = (journeyId: number, entranceId: number) => `curl --request POST \\
--url '${env.api.baseURL}/client/journeys/${journeyId}/trigger' \\
--header 'Authorization: Bearer API_KEY' \\
--header 'Content-Type: application/json' \\
--data '{
    "entrance_id": ${entranceId},
    "user": {
        "external_id": "example-user-id",
        "extra_user_property": true
    },
    "event": {
        "purchase_amount": 29.99
    }
}'`

export const entranceStep: JourneyStepType<EntranceConfig> = {
    name: 'entrance',
    icon: <EntranceStepIcon />,
    category: 'entrance',
    description: 'entrance_desc',
    maxWidth: 800,
    newData: async () => ({
        trigger: 'none',
    }),
    Describe({
        project: {
            id: projectId,
        },
        value: {
            trigger,
            event_name,
            rule,
            list_id,
            schedule,
            multiple,
            concurrent,
            references = [],
        },
    }) {
        const { t } = useTranslation()
        const [preferences] = useContext(PreferencesContext)

        const [list] = useResolver(useCallback(async () => {
            if (trigger === 'schedule' && list_id) {
                return await api.lists.get(projectId, list_id)
            }
            return null
        }, [projectId, list_id, trigger]))

        if (trigger === 'schedule') {
            let s = ''
            if (schedule) {
                try {
                    const rule = RRule.fromString(schedule)
                    s = rruleToText(rule.options, t)
                } catch {}
            }
            return (
                <div style={{ maxWidth: 300 }}>
                    {t('entrance_add_everyone_from') + ' '}
                    <strong>
                        {list?.name ?? <>&#8211;</>}
                    </strong>
                    {' '}
                    {s}
                    {
                        multiple && !concurrent && (
                            <>
                                {' '}
                                {t('entrance_schedule_skips_active')}
                            </>
                        )
                    }
                </div>
            )
        }

        if (trigger === 'event') {
            return (
                <div style={{ maxWidth: 300 }}>
                    {t('entrance_add_everyone_when') + ' '}
                    <strong>{event_name ?? ''}</strong>
                    {t('entrance_occurs')}
                    {
                        rule && isEventWrapper(rule) && !!rule?.children?.length && (
                            <>
                                {' '}
                                {ruleDescription(preferences, rule)}
                            </>
                        )
                    }
                </div>
            )
        }

        if (references.length) {
            return <div className="references">
                <span>{t('entrance_links')}</span>
                <div className="ui-tag-list">
                    {references.map(journey =>
                        <Tag variant="plain" key={journey.id}>
                            <Link to={`../journeys/${journey.id}`}>{journey.name}</Link>
                        </Tag>,
                    )}
                </div>
            </div>
        }

        return <>{t('entrance_empty')}</>
    },
    Edit({ onChange, project: { id: projectId }, journey: { id: journeyId }, stepId, value, disabled }) {

        const { t } = useTranslation()
        const getList = useCallback(async (id: number) => await api.lists.get(projectId, id), [projectId])
        const searchLists = useCallback(async (q: string) => await api.lists.search(projectId, {
            q,
            limit: 50,
            filter: { all_folders: true },
        }), [projectId])
        const [suggestions] = useResolver(useCallback(async () => await api.projects.pathSuggestions(projectId), [projectId]))

        const editSuggestionsValue = useMemo(() => ({ suggestions: suggestions ?? emptySuggestions }), [suggestions])

        return (
            <>
                <RadioInput
                    label={t('trigger')}
                    value={value.trigger}
                    options={getTriggerOptions(t)}
                    onChange={trigger => onChange({ ...value, trigger })}
                    required
                    disabled={disabled}
                />
                {
                    value.trigger === 'event' && (
                        <>
                            <VariablesContext.Provider value={editSuggestionsValue}>
                                <label className="ui-text-input">
                                    <span>
                                        {t('event_name')}
                                        <span style={{ color: 'red' }}>&nbsp;*</span>
                                    </span>
                                    <RuleEventName
                                        value={value.event_name ?? ''}
                                        onChange={event_name => onChange({
                                            ...value,
                                            event_name,
                                            rule: {
                                                ...value.rule ?? createEventRule(),
                                                value: event_name,
                                            },
                                        })}
                                        required
                                        disabled={disabled}
                                        size="regular"
                                    />
                                </label>
                            </VariablesContext.Provider>
                            {
                                value.event_name && (
                                    <RuleBuilder
                                        rule={value.rule ?? createEventRule()}
                                        setRule={rule => onChange({
                                            ...value,
                                            rule: {
                                                ...rule,
                                                value: value.event_name,
                                            },
                                        })}
                                        eventName={value.event_name}
                                        headerPrefix={t('entrance_matching')}
                                        maxRuleSets={-1}
                                        disabled={disabled}
                                    />
                                )
                            }
                            <SwitchField
                                name="multiple"
                                label={t('entrance_multiple_entries')}
                                subtitle={t('entrance_multiple_entries_desc')}
                                checked={Boolean(value.multiple)}
                                onChange={multiple => onChange({ ...value, multiple })}
                                disabled={disabled}
                            />
                            {
                                value.multiple && (
                                    <SwitchField
                                        name="concurrent"
                                        label={t('entrance_simultaneous_entries')}
                                        subtitle={t('entrance_simultaneous_entries_desc')}
                                        checked={Boolean(value.concurrent)}
                                        onChange={concurrent => onChange({ ...value, concurrent })}
                                        disabled={disabled}
                                    />
                                )
                            }
                        </>
                    )
                }
                {
                    value.trigger === 'schedule' && (
                        <>
                            <EntityIdPicker
                                get={getList}
                                search={searchLists}
                                value={value.list_id ?? 0}
                                onChange={list_id => onChange({ ...value, list_id })}
                                label={t('list')}
                                subtitle={t('entrance_list_desc')}
                                required
                                disabled={disabled}
                            />
                            <RRuleEditor
                                label={t('schedule')}
                                value={[value.schedule ?? '', undefined]}
                                onChange={([schedule, rule]) => onChange({ ...value, schedule, multiple: !!rule?.freq })}
                                disabled={disabled}
                            />
                            {
                                value.multiple && (
                                    <SwitchField
                                        name="concurrent"
                                        label={t('entrance_simultaneous_entries')}
                                        subtitle={t('entrance_simultaneous_entries_schedule_desc')}
                                        checked={Boolean(value.concurrent)}
                                        onChange={concurrent => onChange({ ...value, concurrent })}
                                        disabled={disabled}
                                    />
                                )
                            }
                        </>
                    )
                }
                {
                    !!stepId && value.trigger === 'none' && (
                        <div style={{ maxWidth: 600 }}>
                            <CodeExample
                                title={t('entrance_trigger')}
                                description={
                                    <Trans i18nKey="entrance_trigger_desc">
                                        This entrance can be triggered directly via API. An example request is available below. Data from the <code>event</code> field will be available for use in the journey and campaign templates under <code>journey.DATA_KEY_OF_THIS_STEP.*</code> (for example, <code>journey.my_entrance.purchaseAmount</code>).
                                    </Trans>
                                }
                                code={codeExample(journeyId, stepId)}
                            />
                        </div>
                    )
                }
            </>
        )
    },
    hasDataKey: true,
    hideTopHandle: true,
}
